import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Download, AlertTriangle, CheckCircle2, Loader2,
  RefreshCw, ShoppingBag, X, Save, Plus, ChevronUp,
  ChevronDown, ZoomIn, Trash2, Tag, Check,
} from 'lucide-react';
import { api } from '../../lib/api';
import { PLATFORM_NAMES, Platform } from '@crosova/shared';

interface ImportItem {
  sku: string;
  ebayItemId?: string;
  title: string;
  description: string;
  pricePence: number;
  condition: string;
  listingType?: 'fixed' | 'auction' | 'unknown';
  watchCount?: number;
  quantity?: number;
  quantityAvailable?: number;
  timeLeft?: string | null;
  endTime?: string | null;
  brand?: string;
  size?: string;
  colour?: string;
  category?: string;
  tags: string[];
  photoUrls: string[];
  isDuplicate: boolean;
  duplicateId?: string;
}

const CONDITIONS = [
  { value: 'new_with_tags', label: 'New with tags' },
  { value: 'new_without_tags', label: 'New without tags' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
];

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040',
};

function poundFormat(pence: number) {
  return `£${(pence / 100).toFixed(2)}`;
}

// Mirror of backend upgradeEbayImageUrl — upgrades any stored low-res eBay URL to max quality
function upgradeEbayUrl(url: string): string {
  if (!url) return url;
  if (url.includes('/00/s/')) return url.replace(/\$_\d+\./, '$_140.');
  if (url.includes('i.ebayimg.com')) return url.replace(/s-l\d+\./i, 's-l1600.');
  if (url.includes('thumbs.ebaystatic.com')) {
    return url
      .replace('thumbs.ebaystatic.com', 'i.ebayimg.com')
      .replace(/([^/])(s-l\d+\.)/i, '$1/s-l1600.');
  }
  return url;
}

// Parse ISO 8601 duration like "P2DT3H30M" → "2d 3h 30m"
function formatTimeLeft(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = iso.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?/);
  if (!m) return null;
  const days = parseInt(m[1] ?? '0', 10);
  const hours = parseInt(m[2] ?? '0', 10);
  const mins = parseInt(m[3] ?? '0', 10);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins && !days) parts.push(`${mins}m`);
  return parts.join(' ') || null;
}

export function ImportPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeItem, setActiveItem] = useState<ImportItem | null>(null);
  const [importedListingId, setImportedListingId] = useState<string | null>(null);
  // Track SKUs that have been imported this session so we can hide them from the list
  const [importedSkus, setImportedSkus] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const preview = useQuery<{ items: ImportItem[]; total: number }>({
    queryKey: ['import-ebay-preview'],
    queryFn: () => api.get('/import/ebay/preview').then((r) => r.data),
    retry: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const importMutation = useMutation({
    mutationFn: (skus: string[]) =>
      api.post<{ imported: number; skipped: number; listingIds: string[] }>('/import/ebay', { skus }).then((r) => r.data),
    onSuccess: (data, skus) => {
      qc.invalidateQueries({ queryKey: ['listings'] });
      // Remove imported SKUs from the visible list immediately
      setImportedSkus((prev) => new Set([...prev, ...skus]));
      setSelected((prev) => { const next = new Set(prev); skus.forEach((s) => next.delete(s)); return next; });
      if (skus.length === 1 && data.listingIds[0]) {
        setImportedListingId(data.listingIds[0]);
      } else {
        setActiveItem(null);
        setSelected(new Set());
      }
    },
  });

  const allItems = preview.data?.items ?? [];
  // Hide already-imported items and items imported this session
  const items = allItems.filter((i) => !i.isDuplicate && !importedSkus.has(i.sku));
  const importable = items;

  function toggleSelect(sku: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  }

  function openItem(item: ImportItem) {
    setActiveItem(item);
    setImportedListingId(item.isDuplicate && item.duplicateId ? item.duplicateId : null);
  }

  function closePanel() {
    setActiveItem(null);
    setImportedListingId(null);
  }

  const panelOpen = activeItem !== null;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Download size={16} className="text-secondary-400" />
            Import from eBay
          </h1>
          <p className="text-[11.5px] text-white/25 mt-0.5">
            Click any listing to preview and edit before importing
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && !panelOpen && (
            <button
              onClick={() => importMutation.mutate([...selected])}
              disabled={importMutation.isPending}
              className="btn-primary flex items-center gap-1.5 !text-[12px]"
            >
              {importMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              Import {selected.size} selected
            </button>
          )}
          <button
            onClick={() => { setActiveItem(null); setImportedListingId(null); preview.refetch(); }}
            disabled={preview.isFetching}
            className="btn-secondary flex items-center gap-1.5 !text-[11px]"
          >
            <RefreshCw size={12} className={preview.isFetching ? 'animate-spin' : ''} />
            {preview.isFetching ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Platform tabs (eBay only for now) */}
      <div className="flex gap-2 flex-shrink-0">
        <div className="card px-4 py-2 border-primary-500/30 ring-1 ring-primary-500/20 flex items-center gap-2">
          <div className="w-6 h-6 rounded text-[9px] font-bold flex items-center justify-center text-white" style={{ background: '#e53935' }}>eB</div>
          <span className="text-[12px] font-medium">eBay</span>
          {preview.data && <span className="text-[10px] text-white/30">{items.length} to import{allItems.filter(i=>i.isDuplicate).length > 0 ? ` · ${allItems.filter(i=>i.isDuplicate).length} already in Crosova` : ''}</span>}
        </div>
        {(['Vinted', 'Depop', 'Etsy'] as const).map((name) => (
          <div key={name} className="card px-4 py-2 opacity-40 flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-white/[0.07] flex items-center justify-center text-[9px] font-bold text-white/40">{name.slice(0, 2)}</div>
            <span className="text-[12px] text-white/40">{name}</span>
            <span className="text-[9.5px] text-white/20 border border-white/10 rounded px-1">Soon</span>
          </div>
        ))}
      </div>

      {/* Main area: list + panel */}
      <div className="flex gap-4 items-start">

        {/* ── Listing list ── */}
        <div className={`flex flex-col gap-0 transition-all duration-200 ${panelOpen ? 'w-[45%] flex-shrink-0' : 'flex-1'}`}>

          {preview.isLoading && (
            <div className="card p-10 flex flex-col items-center gap-3">
              <Loader2 size={28} className="text-primary-400 animate-spin" />
              <p className="text-sm text-white/40">Fetching your eBay listings…</p>
              <p className="text-[11px] text-white/25">This pulls full details including photos — takes ~10 seconds</p>
            </div>
          )}

          {preview.isError && (
            <div className="card p-5 flex items-start gap-3">
              <AlertTriangle size={16} className="text-danger mt-0.5" />
              <div>
                <p className="text-sm font-medium text-danger">Could not fetch eBay listings</p>
                <p className="text-[11.5px] text-white/40 mt-1">{(preview.error as any)?.response?.data?.error ?? 'Make sure eBay is connected in Settings → Platforms.'}</p>
                <button onClick={() => preview.refetch()} className="btn-secondary text-[12px] mt-3 flex items-center gap-1.5">
                  <RefreshCw size={12} /> Try again
                </button>
              </div>
            </div>
          )}

          {!preview.isLoading && !preview.isError && items.length === 0 && (
            <div className="card p-10 text-center">
              <ShoppingBag size={28} className="text-white/10 mx-auto mb-3" />
              <p className="text-sm text-white/40">No active listings found on eBay</p>
            </div>
          )}

          {items.length > 0 && (
            <div className="card overflow-hidden">
              {/* Select all header */}
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.05]">
                <button
                  onClick={() => selected.size > 0 ? setSelected(new Set()) : setSelected(new Set(importable.map((i) => i.sku)))}
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                    selected.size > 0 ? 'bg-primary-500 border-primary-500' : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  {selected.size > 0 && <span className="text-white text-[9px]">✓</span>}
                </button>
                <span className="text-[10.5px] text-white/30 flex-1">
                  {selected.size > 0 ? `${selected.size} selected` : `${importable.length} ready to import`}
                </span>
              </div>

              {items.map((item) => {
                const isActive = activeItem?.sku === item.sku;
                return (
                  <div
                    key={item.sku}
                    onClick={() => openItem(item)}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-white/[0.04] last:border-0 transition-colors ${
                      isActive ? 'bg-primary-500/[0.08] border-l-2 border-l-primary-500' : 'hover:bg-white/[0.025]'
                    } ${item.isDuplicate ? 'opacity-60' : ''}`}
                  >
                    {/* Checkbox — shows ✓ for already-imported, selectable for new */}
                    {item.isDuplicate ? (
                      <div className="w-3.5 h-3.5 rounded border border-secondary-400/40 bg-secondary-400/10 flex-shrink-0 flex items-center justify-center">
                        <Check size={8} className="text-secondary-400" />
                      </div>
                    ) : (
                      <div
                        onClick={(e) => { e.stopPropagation(); toggleSelect(item.sku); }}
                        className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center cursor-pointer ${
                          selected.has(item.sku) ? 'bg-primary-500 border-primary-500' : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        {selected.has(item.sku) && <span className="text-white text-[9px]">✓</span>}
                      </div>
                    )}

                    {/* Cover photo */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-white/[0.06]">
                      {item.photoUrls[0] && <img src={upgradeEbayUrl(item.photoUrls[0])} alt="" className="w-full h-full object-cover" />}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[12.5px] text-white/80 truncate font-medium">{item.title}</span>
                        {item.listingType === 'auction' && (
                          <span className="flex-shrink-0 text-[8.5px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-400 border border-amber-400/25 font-semibold tracking-wide">AUC</span>
                        )}
                        {item.listingType === 'fixed' && (
                          <span className="flex-shrink-0 text-[8.5px] px-1 py-0.5 rounded bg-primary-500/15 text-primary-300 border border-primary-500/25">BIN</span>
                        )}
                      </div>
                      <div className="text-[10.5px] text-white/30 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="capitalize">{item.condition.replace(/_/g, ' ')}</span>
                        {item.brand && <span>· {item.brand}</span>}
                        {item.size && <span>· {item.size}</span>}
                        {(item.watchCount ?? 0) > 0 && <span className="text-primary-400">· {item.watchCount} watching</span>}
                        {(item.quantityAvailable ?? 1) > 1 && <span>· Qty: {item.quantityAvailable}</span>}
                        {item.timeLeft && formatTimeLeft(item.timeLeft) && <span className="text-amber-400/70">· {formatTimeLeft(item.timeLeft)} left</span>}
                      </div>
                    </div>

                    {/* Price + import status */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="text-[13px] font-semibold text-secondary-400">{poundFormat(item.pricePence)}</div>
                      {item.isDuplicate ? (
                        <span className="text-[9.5px] text-secondary-400 flex items-center gap-0.5">
                          <Check size={9} /> In Crosova
                        </span>
                      ) : (
                        <span className="text-[9.5px] text-white/25">Click to import</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Detail panel — sticky so it stays in viewport as list scrolls ── */}
        {panelOpen && (
          <div key={activeItem?.sku ?? importedListingId ?? 'panel'} className="flex-1 sticky top-0" style={{ maxHeight: 'calc(100vh - 92px)', overflowY: 'auto' }}>
            {importedListingId
              ? <CrosavoListingPanel listingId={importedListingId} onClose={closePanel} />
              : activeItem && (
                <EbayPreviewPanel
                  key={activeItem.sku}
                  item={activeItem}
                  onClose={closePanel}
                  onImported={(listingId) => setImportedListingId(listingId)}
                />
              )
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ── eBay preview panel (before import) ──────────────────────────────────────

function EbayPreviewPanel({ item, onClose, onImported }: {
  item: ImportItem;
  onClose: () => void;
  onImported: (listingId: string) => void;
}) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: item.title,
    description: item.description,
    price: item.pricePence ? (item.pricePence / 100).toFixed(2) : '',
    condition: item.condition,
    brand: item.brand ?? '',
    size: item.size ?? '',
    colour: item.colour ?? '',
    category: item.category ?? '',
    tags: item.tags ?? [] as string[],
  });
  const [tagInput, setTagInput] = useState('');

  const importSingle = useMutation({
    mutationFn: () =>
      api.post<{ imported: number; skipped: number; listingIds: string[] }>('/import/ebay', { skus: [item.sku] }).then((r) => r.data),
    onSuccess: async (data) => {
      if (data.listingIds[0]) {
        // Apply any edits the user made
        await api.patch(`/listings/${data.listingIds[0]}`, {
          title: form.title,
          description: form.description,
          price: Math.round(parseFloat(form.price || '0') * 100),
          condition: form.condition,
          brand: form.brand || undefined,
          size: form.size || undefined,
          colour: form.colour || undefined,
          category: form.category || undefined,
          tags: form.tags,
        });
        onImported(data.listingIds[0]);
      }
    },
  });

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput('');
  }

  return (
    <div className="card flex flex-col h-full">
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/50 hover:text-white" onClick={() => setLightbox(null)}><X size={24} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Panel header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.05] flex-shrink-0 min-w-0">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-medium text-white/70 truncate">{item.title}</p>
          <p className="text-[10px] text-white/30 mt-0.5">Edit details before importing</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => importSingle.mutate()}
            disabled={importSingle.isPending}
            className="btn-primary flex items-center gap-1.5 !text-[12px] whitespace-nowrap"
          >
            {importSingle.isPending ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            {importSingle.isPending ? 'Importing...' : 'Import'}
          </button>
          <button onClick={onClose} className="p-1.5 text-white/30 hover:text-white/60 flex-shrink-0"><X size={15} /></button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 p-4 flex flex-col gap-4">
        {/* Photos */}
        <div>
          <div className="text-[10.5px] font-semibold text-white/30 uppercase tracking-wide mb-2">Photos ({item.photoUrls.length}) — click to view full size</div>
          <div className="grid grid-cols-3 gap-1.5">
            {item.photoUrls.slice(0, 9).map((url, i) => {
              const hiRes = upgradeEbayUrl(url);
              return (
              <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-white/[0.04] cursor-pointer" onClick={() => setLightbox(hiRes)}>
                <img src={hiRes} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn size={16} className="text-white" />
                </div>
                {i === 0 && <div className="absolute bottom-1 left-1 text-[8px] bg-black/60 text-white/70 px-1 rounded">Cover</div>}
              </div>
              );
            })}
          </div>
        </div>

        {/* Editable fields */}
        <PanelField label={`Title (${form.title.length}/80)`}>
          <input className="input" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} maxLength={80} />
        </PanelField>

        <PanelField label="Description">
          <textarea className="input min-h-[100px] resize-y" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
        </PanelField>

        <div className="grid grid-cols-2 gap-3">
          <PanelField label="Price (£)">
            <input className="input" type="number" step="0.01" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} />
          </PanelField>
          <PanelField label="Condition">
            <select className="input" value={form.condition} onChange={(e) => setForm(f => ({ ...f, condition: e.target.value }))}>
              {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </PanelField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <PanelField label="Brand"><input className="input" value={form.brand} onChange={(e) => setForm(f => ({ ...f, brand: e.target.value }))} /></PanelField>
          <PanelField label="Size"><input className="input" value={form.size} onChange={(e) => setForm(f => ({ ...f, size: e.target.value }))} /></PanelField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <PanelField label="Colour"><input className="input" value={form.colour} onChange={(e) => setForm(f => ({ ...f, colour: e.target.value }))} /></PanelField>
          <PanelField label="Category"><input className="input" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} /></PanelField>
        </div>

        <PanelField label="Tags">
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {form.tags.map(tag => (
              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/25 text-[11px] text-primary-300">
                {tag}<button onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))}><X size={9} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="input flex-1 !text-[12px]" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }} placeholder="Add tag, press Enter" />
            <button onClick={addTag} className="btn-secondary !px-2"><Tag size={12} /></button>
          </div>
        </PanelField>

        <button
          onClick={() => importSingle.mutate()}
          disabled={importSingle.isPending}
          className="btn-primary w-full flex items-center justify-center gap-2 mt-2"
        >
          {importSingle.isPending ? <><Loader2 size={14} className="animate-spin" /> Importing...</> : <><Download size={14} /> Import this listing</>}
        </button>
      </div>
    </div>
  );
}

// ── Crosova listing edit panel (after import or for duplicates) ──────────────

function CrosavoListingPanel({ listingId, onClose }: { listingId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: listing, isLoading } = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => api.get(`/listings/${listingId}`).then((r) => r.data),
    enabled: !!listingId,
  });

  const { data: allPlatforms = [] } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.get('/platforms').then((r) => r.data),
  });
  const connectedPlatforms: string[] = (allPlatforms as any[]).filter((p: any) => p.connected).map((p: any) => p.platform);

  const [form, setForm] = useState({ title: '', description: '', price: '', condition: 'good', brand: '', size: '', colour: '', category: '', sku: '', tags: [] as string[] });
  const [tagInput, setTagInput] = useState('');
  const [photos, setPhotos] = useState<{ id: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set());
  const [syncingPlatform, setSyncingPlatform] = useState<string | null>(null);

  useState(() => {
    if (listing) {
      setForm({ title: listing.title ?? '', description: listing.description ?? '', price: listing.price ? (listing.price / 100).toFixed(2) : '', condition: listing.condition ?? 'good', brand: listing.brand ?? '', size: listing.size ?? '', colour: listing.colour ?? '', category: listing.category ?? '', sku: listing.sku ?? '', tags: listing.tags ?? [] });
      setPhotos([...(listing.photos ?? [])].sort((a: any, b: any) => a.position - b.position));
    }
  });

  // Re-populate when listing loads
  const [seenId, setSeenId] = useState<string | null>(null);
  if (listing && listing.id !== seenId) {
    setSeenId(listing.id);
    setForm({ title: listing.title ?? '', description: listing.description ?? '', price: listing.price ? (listing.price / 100).toFixed(2) : '', condition: listing.condition ?? 'good', brand: listing.brand ?? '', size: listing.size ?? '', colour: listing.colour ?? '', category: listing.category ?? '', sku: listing.sku ?? '', tags: listing.tags ?? [] });
    setPhotos([...(listing.photos ?? [])].sort((a: any, b: any) => a.position - b.position));
  }

  const save = useMutation({
    mutationFn: () => api.patch(`/listings/${listingId}`, { title: form.title, description: form.description, price: Math.round(parseFloat(form.price || '0') * 100), condition: form.condition, brand: form.brand || undefined, size: form.size || undefined, colour: form.colour || undefined, category: form.category || undefined, sku: form.sku || undefined, tags: form.tags }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['listings'] }); onClose(); },
  });

  const sync = useMutation({
    mutationFn: (platform: string) => api.post(`/listings/${listingId}/crosslist`, { platforms: [platform] }).then((r) => r.data),
    onMutate: (p) => setSyncingPlatform(p),
    onSettled: () => setSyncingPlatform(null),
  });

  const crosslist = useMutation({
    mutationFn: (platforms: string[]) => api.post(`/listings/${listingId}/crosslist`, { platforms }).then((r) => r.data),
    onSuccess: () => setSelectedPlatforms(new Set()),
  });

  const deletePhoto = useMutation({
    mutationFn: (photoId: string) => api.delete(`/listings/${listingId}/photos/${photoId}`),
    onSuccess: (_, photoId) => setPhotos((prev) => prev.filter((p) => p.id !== photoId)),
  });

  const reorderPhotos = useMutation({
    mutationFn: (ordered: { id: string }[]) => api.post(`/listings/${listingId}/photos/reorder`, { photoIds: ordered.map((p) => p.id) }),
  });

  function movePhoto(from: number, to: number) {
    const next = [...photos];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setPhotos(next);
    reorderPhotos.mutate(next);
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || photos.length >= 12) return;
    setUploading(true);
    setUploadError('');
    try {
      const uploaded: { id: string; url: string }[] = [];
      for (const file of files.slice(0, 12 - photos.length)) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post<{ url: string; s3Key: string }>('/uploads/photo', fd);
        const attached = await api.post<{ id: string; url: string }>(`/listings/${listingId}/photos/attach`, { url: res.data.url, s3Key: res.data.s3Key });
        uploaded.push(attached.data);
      }
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? `Upload failed (${err?.response?.status ?? 'network error'})`;
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput('');
  }

  const platformConnections: any[] = listing?.platformListings ?? [];

  if (isLoading) return <div className="card p-10 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-white/30" /></div>;

  return (
    <div className="card flex flex-col h-full">
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/50 hover:text-white" onClick={() => setLightbox(null)}><X size={24} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.05] flex-shrink-0 min-w-0">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-medium text-white/70 truncate">{listing?.title}</p>
          <p className="text-[10px] text-white/30 mt-0.5 capitalize">{listing?.status} · In Crosova</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => save.mutate()} disabled={save.isPending} className={`btn-primary flex items-center gap-1.5 !text-[12px] whitespace-nowrap ${saved ? '!bg-secondary-500/80' : ''}`}>
            {save.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saved ? 'Saved ✓' : 'Save'}
          </button>
          <button onClick={onClose} className="p-1.5 text-white/30 hover:text-white/60 flex-shrink-0"><X size={15} /></button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 p-4 flex flex-col gap-4">

        {/* Platform connections */}
        {platformConnections.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-white/[0.02] rounded-xl border border-white/[0.05]">
            {platformConnections.map((pl: any) => (
              <div key={pl.platform} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center text-white" style={{ background: PLATFORM_COLOURS[pl.platform] ?? '#555' }}>
                  {(PLATFORM_NAMES[pl.platform as Platform] ?? pl.platform).slice(0, 2)}
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${pl.status === 'live' ? 'bg-secondary-400/10 text-secondary-400 border-secondary-400/20' : pl.status === 'failed' ? 'bg-danger/10 text-danger border-danger/20' : 'bg-white/[0.04] text-white/30 border-white/10'}`}>{pl.status}</span>
                {['live', 'failed'].includes(pl.status) && (
                  <button onClick={() => sync.mutate(pl.platform)} disabled={syncingPlatform === pl.platform} className="text-[10px] text-secondary-400 hover:text-secondary-300 flex items-center gap-0.5">
                    <RefreshCw size={10} className={syncingPlatform === pl.platform ? 'animate-spin' : ''} />
                    {pl.status === 'live' ? 'Sync' : 'Retry'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Photos */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] font-semibold text-white/30 uppercase tracking-wide">Photos ({photos.length}/12)</div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || photos.length >= 12}
              className="btn-secondary !text-[10px] !py-1 flex items-center gap-1"
            >
              {uploading ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
              {uploading ? 'Uploading...' : 'Add photos'}
            </button>
            <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </div>
          {uploadError && (
            <div className="text-[11px] text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2 mb-2">
              {uploadError}
            </div>
          )}
          {photos.length === 0 ? (
            <label className="block border-2 border-dashed border-white/10 rounded-xl p-6 text-center cursor-pointer hover:border-white/20">
              <Plus size={20} className="mx-auto text-white/20 mb-1" />
              <p className="text-[11px] text-white/30">Add photos</p>
              <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {photos.map((photo, idx) => (
                <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-white/[0.04]">
                  <img src={upgradeEbayUrl(photo.url)} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && <div className="absolute top-1 left-1 text-[7px] bg-primary-500/80 text-white px-1 rounded">Cover</div>}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                    <div className="flex gap-1">
                      <button onClick={() => setLightbox(upgradeEbayUrl(photo.url))} className="w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><ZoomIn size={10} className="text-white" /></button>
                      <button onClick={() => deletePhoto.mutate(photo.id)} className="w-6 h-6 rounded-full bg-danger/30 hover:bg-danger/50 flex items-center justify-center"><Trash2 size={10} className="text-white" /></button>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => movePhoto(idx, idx - 1)} disabled={idx === 0} className="w-5 h-5 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-20"><ChevronUp size={9} className="text-white -rotate-90" /></button>
                      <button onClick={() => movePhoto(idx, idx + 1)} disabled={idx === photos.length - 1} className="w-5 h-5 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-20"><ChevronDown size={9} className="text-white -rotate-90" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {photos.length < 12 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-white/10 hover:border-white/25 flex items-center justify-center cursor-pointer">
                  <Plus size={16} className="text-white/20" />
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              )}
            </div>
          )}
        </div>

        {/* Fields */}
        <PanelField label={`Title (${form.title.length}/80)`}>
          <input className="input" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} maxLength={80} />
        </PanelField>
        <PanelField label="Description">
          <textarea className="input min-h-[100px] resize-y" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
        </PanelField>
        <div className="grid grid-cols-2 gap-3">
          <PanelField label="Price (£)"><input className="input" type="number" step="0.01" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} /></PanelField>
          <PanelField label="Condition">
            <select className="input" value={form.condition} onChange={(e) => setForm(f => ({ ...f, condition: e.target.value }))}>
              {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </PanelField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <PanelField label="Brand"><input className="input" value={form.brand} onChange={(e) => setForm(f => ({ ...f, brand: e.target.value }))} /></PanelField>
          <PanelField label="Size"><input className="input" value={form.size} onChange={(e) => setForm(f => ({ ...f, size: e.target.value }))} /></PanelField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <PanelField label="Colour"><input className="input" value={form.colour} onChange={(e) => setForm(f => ({ ...f, colour: e.target.value }))} /></PanelField>
          <PanelField label="Category"><input className="input" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} /></PanelField>
        </div>

        {/* Tags */}
        <PanelField label="Tags">
          <div className="flex flex-wrap gap-1 mb-1.5">
            {form.tags.map(tag => (
              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/25 text-[11px] text-primary-300">
                {tag}<button onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))}><X size={9} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="input flex-1 !text-[12px]" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }} placeholder="Add tag, Enter to add" />
            <button onClick={addTag} className="btn-secondary !px-2"><Tag size={12} /></button>
          </div>
        </PanelField>

        {/* Publish */}
        {connectedPlatforms.length > 0 && (
          <div className="border-t border-white/[0.05] pt-4">
            <div className="text-[10.5px] font-semibold text-white/30 uppercase tracking-wide mb-2">Publish to platforms</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {connectedPlatforms.map((p) => {
                const existing = platformConnections.find((pl) => pl.platform === p);
                const isOn = selectedPlatforms.has(p);
                return (
                  <button key={p} onClick={() => { const next = new Set(selectedPlatforms); next.has(p) ? next.delete(p) : next.add(p); setSelectedPlatforms(next); }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] transition-colors ${isOn ? 'border-secondary-400/30 bg-secondary-400/[0.06] text-secondary-400' : 'border-white/[0.07] text-white/50 hover:border-white/20'}`}
                  >
                    <div className="w-4 h-4 rounded text-[7px] font-bold flex items-center justify-center text-white" style={{ background: PLATFORM_COLOURS[p] ?? '#555' }}>
                      {(PLATFORM_NAMES[p as Platform] ?? p).slice(0, 2)}
                    </div>
                    {PLATFORM_NAMES[p as Platform] ?? p}
                    {existing && <span className="text-[8.5px] text-amber-400">·{existing.status}</span>}
                    {isOn && <Check size={10} />}
                  </button>
                );
              })}
            </div>
            <button onClick={() => crosslist.mutate([...selectedPlatforms])} disabled={selectedPlatforms.size === 0 || crosslist.isPending} className="btn-primary w-full !text-[12px] flex items-center justify-center gap-1.5">
              {crosslist.isPending ? <><Loader2 size={12} className="animate-spin" /> Queueing...</> : selectedPlatforms.size === 0 ? 'Select platforms above' : `Publish to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PanelField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-medium text-white/30 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}
